import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Battery power-threshold bar — a stacked 0–100% strip: shutdown (red,
 * 0→shutdown%), low-power (orange, →low%) and normal (green) zones. When the
 * recovery ("out") thresholds are supplied, the hysteresis windows between
 * enter and recover render as striped segments — the same graphic the profile
 * wizard's threshold editor draws (it overlays its drag markers on this bar).
 *
 *   <app-power-threshold-bar [shutdownPct]="5" [lowPct]="20" />
 *   <app-power-threshold-bar [shutdownPct]="5" [shutdownOutPct]="10"
 *                            [lowPct]="20" [lowOutPct]="25" />
 *
 * Colorscale primitives are used directly (there are no semantic tokens for
 * band fills); everything else rides on semantic vars so dark theme works.
 */
@Component({
  selector: 'app-power-threshold-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div>
      <div
        class="flex overflow-hidden rounded-full shadow-hairline"
        [style.height.px]="height()"
      >
        <div class="seg-no-power" [style.width.%]="widths().noPower"></div>
        <div class="hyst-no" [style.width.%]="widths().noHyst"></div>
        <div class="seg-low-power" [style.width.%]="widths().lowPower"></div>
        <div class="hyst-low" [style.width.%]="widths().lowHyst"></div>
        <div class="seg-normal" [style.width.%]="widths().normal"></div>
      </div>
      @if (labels()) {
        <!-- Labels sit at their true % along the bar, not evenly spaced. -->
        <div class="relative mt-1.5 h-3.5 font-num text-[10px] font-semibold text-ink-3">
          <span class="absolute left-0">0%</span>
          <span class="absolute -translate-x-1/2" [style.left.%]="shutdown()">{{ shutdown() }}%</span>
          <span class="absolute -translate-x-1/2" [style.left.%]="low()">{{ low() }}%</span>
          <span class="absolute right-0">100%</span>
        </div>
      }
    </div>
  `,
  styles: `
    .seg-no-power {
      background: var(--colorscales-red-300);
    }
    .seg-low-power {
      background: var(--colorscales-orange-300);
    }
    .seg-normal {
      background: var(--colorscales-green-500);
    }
    .hyst-no {
      background: repeating-linear-gradient(
        135deg,
        var(--colorscales-red-100) 0 5px,
        var(--colorscales-orange-100) 5px 10px
      );
    }
    .hyst-low {
      background: repeating-linear-gradient(
        135deg,
        var(--colorscales-orange-100) 0 5px,
        var(--colorscales-green-100) 5px 10px
      );
    }
  `,
})
export class PowerThresholdBar {
  /** Battery % at/below which the device shuts down. */
  readonly shutdownPct = input.required<number>();
  /** Battery % at/below which the device enters low-power. */
  readonly lowPct = input.required<number>();
  /** Recovery % to leave shutdown (draws the hysteresis stripe when set). */
  readonly shutdownOutPct = input<number | null>(null);
  /** Recovery % to leave low-power (draws the hysteresis stripe when set). */
  readonly lowOutPct = input<number | null>(null);

  readonly height = input(10);
  /** Render the 0%/threshold/100% scale row under the bar. */
  readonly labels = input(true);

  private clamp = (n: number) => Math.min(100, Math.max(0, n));
  protected readonly shutdown = computed(() => this.clamp(this.shutdownPct()));
  protected readonly low = computed(() => this.clamp(this.lowPct()));
  private readonly shutdownOut = computed(() =>
    this.clamp(this.shutdownOutPct() ?? this.shutdown()),
  );
  private readonly lowOut = computed(() => this.clamp(this.lowOutPct() ?? this.low()));

  /** Segment widths in percent points, clamped at 0 so out-of-contract data
   *  (overlapping bands) degrades to a squashed render instead of negatives. */
  protected readonly widths = computed(() => {
    const npEnter = this.shutdown();
    const npExit = this.shutdownOut();
    const lpEnter = this.low();
    const lpExit = this.lowOut();
    return {
      noPower: Math.max(0, npEnter),
      noHyst: Math.max(0, npExit - npEnter),
      lowPower: Math.max(0, lpEnter - npExit),
      lowHyst: Math.max(0, lpExit - lpEnter),
      normal: Math.max(0, 100 - lpExit),
    };
  });
}
